import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import { BASE_URL } from "../../Shared/URL.js";
import CustomDatePicker from "../CustomDate/CustomDatePicker.jsx";

export default function CreateIncome() {
  const [errorMsg, setErrorMsg] = useState(null); // رسالة الخطأ
  const [isLoading, setIsLoading] = useState(false); // حالة التحميل
  const navigate = useNavigate(); // للتنقل بين الصفحات

  // ✅ التوكن
  const token = localStorage.getItem("__inventory_sys_auth_token_5f9dA72xQp__");

  // ✅ إنشاء axios instance مع التوكن
  const axiosInstance = axios.create({
    baseURL: BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  // ✅ أنواع الإيرادات
  const incomeTypes = [
    { value: 0, label: "🎁 مكافأة" },
    { value: 1, label: "💰 عمولة" },
    { value: 2, label: "📦 أخرى" },
  ];

  // ✅ طرق الدفع
  const paymentMethods = [
    { value: 0, label: "💵 نقداً" },
    { value: 1, label: "📱 فودافون كاش" },
    { value: 2, label: "💳 إنستاباي" },
  ];

  // ✅ Formik مع التحقق من صحة البيانات باستخدام Yup
  const formik = useFormik({
    initialValues: {
      type: "",
      amount: "",
      date: new Date().toISOString().split("T")[0],
      method: "",
      notes: "",
    },
    validationSchema: Yup.object({
      type: Yup.string().required("نوع الإيراد مطلوب"),
      amount: Yup.number()
        .required("المبلغ مطلوب")
        .positive("المبلغ يجب أن يكون موجباً")
        .min(0.01, "المبلغ يجب أن يكون على الأقل 0.01"),
      date: Yup.string().required("التاريخ مطلوب"),
      method: Yup.string().required("طريقة الدفع مطلوبة"),
      notes: Yup.string().max(500, "الملاحظات لا تتجاوز 500 حرف"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      setErrorMsg(null);
      
      try {
        const body = {
          type: parseInt(values.type),
          amount: parseFloat(values.amount),
          date: new Date(values.date).toISOString(),
          method: parseInt(values.method),
          notes: values.notes.trim() || null,
        };

        const { data } = await axiosInstance.post("/Income/incomes", body);

        if (data?.status === 200 || data) {
          await Swal.fire({
            title: "نجاح!",
            text: data.message || "تم إنشاء الإيراد بنجاح",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });

          navigate("/income"); // الرجوع لصفحة الإيرادات
        }
      } catch (error) {
        console.error("خطأ في إنشاء الإيراد:", error);
        const serverMsg = 
          error?.response?.data?.message || 
          error?.response?.data?.error ||
          "حدث خطأ ما. حاول مرة أخرى.";
        setErrorMsg(serverMsg);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container py-5" style={{ maxWidth: "700px" }}>
        
        {/* الهيدر */}
        <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
          <div>
            <h2 style={{ color: "#16a34a", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
              <i className="bi bi-cash-coin me-2"></i>إنشاء مكافأة جديدة
            </h2>
            <small style={{ color: "#64748b", fontSize: "14px" }}>
              تسجيل معاملة إيراد جديدة
            </small>
          </div>
          <button
            type="button"
            onClick={() => navigate("/income")}
            style={{
              background: "#64748b",
              color: "#fff",
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              fontSize: "15px",
              cursor: "pointer",
            }}
          >
            <i className="bi bi-arrow-left me-1"></i> العودة
          </button>
        </div>

        {/* رسالة الخطأ */}
        {errorMsg && (
          <div style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            fontSize: "14px",
            fontWeight: "600",
          }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {errorMsg}
          </div>
        )}

        {/* نموذج إنشاء الإيراد */}
        <div style={cardStyle}>
          <form onSubmit={formik.handleSubmit}>
            
            {/* نوع الإيراد */}
            <div className="mb-4">
              <label style={labelStyle}>
                <i className="bi bi-tag me-2"></i>نوع الإيراد
              </label>
              <select
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{
                  ...inputStyle,
                  borderColor: formik.touched.type && formik.errors.type ? "#16a34a" : "#cbd5e1",
                }}
              >
                <option value="">-- اختر نوع الإيراد --</option>
                {incomeTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              {formik.touched.type && formik.errors.type && (
                <div style={errorStyle}>
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {formik.errors.type}
                </div>
              )}
            </div>

            {/* المبلغ */}
            <div className="mb-4">
              <label style={labelStyle}>
                <i className="bi bi-currency-dollar me-2"></i>المبلغ (جنيه)
              </label>
              <input
                type="number"
                name="amount"
                placeholder="أدخل المبلغ"
                step="0.01"
                min="0"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                onWheel={(e) => e.target.blur()}
                style={{
                  ...inputStyle,
                  borderColor: formik.touched.amount && formik.errors.amount ? "#16a34a" : "#cbd5e1",
                }}
              />
              {formik.touched.amount && formik.errors.amount && (
                <div style={errorStyle}>
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {formik.errors.amount}
                </div>
              )}
            </div>

            {/* التاريخ */}
            <div className="mb-4 w-100">
              <CustomDatePicker
                label="التاريخ"
                value={formik.values.date}
                onChange={(date) => formik.setFieldValue("date", date)}
                onBlur={() => formik.setFieldTouched("date", true)}
                placeholder="اختر التاريخ"
                error={formik.touched.date && formik.errors.date}
              />
              {formik.touched.date && formik.errors.date && (
                <div style={errorStyle}>
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {formik.errors.date}
                </div>
              )}
            </div>

            {/* طريقة الدفع */}
            <div className="mb-4">
              <label style={labelStyle}>
                <i className="bi bi-credit-card me-2"></i>طريقة الدفع
              </label>
              <select
                name="method"
                value={formik.values.method}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{
                  ...inputStyle,
                  borderColor: formik.touched.method && formik.errors.method ? "#16a34a" : "#cbd5e1",
                }}
              >
                <option value="">-- اختر طريقة الدفع --</option>
                {paymentMethods.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              {formik.touched.method && formik.errors.method && (
                <div style={errorStyle}>
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {formik.errors.method}
                </div>
              )}
            </div>

            {/* الملاحظات */}
            <div className="mb-4">
              <label style={labelStyle}>
                <i className="bi bi-pencil-square me-2"></i>ملاحظات (اختياري)
              </label>
              <textarea
                name="notes"
                placeholder="أضف أي ملاحظات إضافية..."
                rows="4"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  minHeight: "100px",
                  borderColor: formik.touched.notes && formik.errors.notes ? "#16a34a" : "#cbd5e1",
                }}
              />
              {formik.touched.notes && formik.errors.notes && (
                <div style={errorStyle}>
                  <i className="bi bi-exclamation-circle me-1"></i>
                  {formik.errors.notes}
                </div>
              )}
            </div>

            {/* ملخص الإيراد */}
            {formik.values.amount && formik.values.type !== "" && formik.values.method !== "" && (
              <div style={{
                background: "#f0fdf4",
                border: "2px solid #86efac",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
              }}>
                <h6 style={{ color: "#166534", fontWeight: "700", fontSize: "15px", marginBottom: "12px" }}>
                  <i className="bi bi-info-circle me-2"></i>ملخص الإيراد
                </h6>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "14px" }}>
                    النوع:
                  </span>
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "15px" }}>
                    {incomeTypes.find(t => t.value === parseInt(formik.values.type))?.label || "-"}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "14px" }}>
                    المبلغ:
                  </span>
                  <span style={{ color: "#14532d", fontWeight: "700", fontSize: "20px" }}>
                    {parseFloat(formik.values.amount || 0).toFixed(2)} ج.م
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "14px" }}>
                    الطريقة:
                  </span>
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "15px" }}>
                    {paymentMethods.find(m => m.value === parseInt(formik.values.method))?.label || "-"}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "14px" }}>
                    التاريخ:
                  </span>
                  <span style={{ color: "#14532d", fontWeight: "600", fontSize: "15px" }}>
                    {new Date(formik.values.date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}

            {/* زر الإرسال */}
            <button
              type="submit"
              disabled={!(formik.dirty && formik.isValid) || isLoading}
              style={{
                width: "100%",
                background: formik.dirty && formik.isValid && !isLoading
                  ? "linear-gradient(135deg, #16a34a 0%, #15803d 100%)"
                  : "#94a3b8",
                color: "#fff",
                padding: "16px",
                borderRadius: "8px",
                border: "none",
                fontWeight: "700",
                fontSize: "18px",
                cursor: formik.dirty && formik.isValid && !isLoading ? "pointer" : "not-allowed",
                boxShadow: formik.dirty && formik.isValid && !isLoading
                  ? "0 4px 12px rgba(22, 163, 74, 0.3)"
                  : "none",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {isLoading ? (
                <>
                  <Bars height="25" width="25" color="#fff" ariaLabel="loading" />
                  <span>جاري الإنشاء...</span>
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle"></i>
                  <span>إنشاء الإيراد</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* بطاقة المعلومات */}
        <div style={{
          background: "#fff",
          border: "1px solid #d1fae5",
          borderRadius: "8px",
          padding: "16px",
          marginTop: "20px",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
        }}>
          <div className="d-flex align-items-start gap-3">
            <div style={{
              background: "#d1fae5",
              color: "#166534",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
              flexShrink: 0,
            }}>
              <i className="bi bi-info-circle"></i>
            </div>
            <div>
              <h6 style={{ color: "#166534", fontWeight: "600", fontSize: "15px", marginBottom: "6px" }}>
                معلومات الإيراد
              </h6>
              <p style={{ color: "#64748b", fontSize: "13px", margin: 0, lineHeight: "1.6" }}>
                سيتم تسجيل جميع الإيرادات في النظام. تأكد من صحة جميع المعلومات قبل الإرسال. يمكنك إضافة ملاحظات اختيارية للرجوع إليها مستقبلاً.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ✅ التنسيقات
const cardStyle = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: "12px",
  padding: "32px",
  marginBottom: "20px",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
};

const labelStyle = {
  color: "#475569",
  fontSize: "15px",
  fontWeight: "600",
  marginBottom: "8px",
  display: "block",
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: "2px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "15px",
  color: "#1e293b",
  outline: "none",
  transition: "all 0.2s",
  fontWeight: "500",
};

const errorStyle = {
  color: "#16a34a",
  fontSize: "13px",
  marginTop: "6px",
  fontWeight: "600",
};